// Live availability route (AVL-001–AVL-019; bead hf-e5-availability-ccp.1).
//
// The browser composes server-decided affordances with the dated editor. It does
// not infer who may edit or withdraw a batch, and every write still crosses the
// boundary-wrapped Convex mutation that owns the authorization decision.

import { Skeleton, StatusMessage } from "@herefor/design-system";
import {
  instant,
  localDateOf,
  startOfLocalWeek,
  timeZone,
  toWallClock,
} from "@herefor/domain-kernel";
import { AVAILABILITY_SURFACE_MESSAGES as COPY } from "@herefor/product-language";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import { circleSettingsQuery } from "../../convex/circleSettings";
import {
  availabilityBatchDocumentId,
  availabilityMutations,
  availabilityPersonDocumentId,
} from "../../convex/availability";
import type { LivePlanResult } from "../../convex/plan";
import { AvailabilityDraftPanel, useConnectivity } from "../../offline/offline";
import {
  AvailabilityEntry,
  AvailabilityOverview,
  AvailabilityRenewalPrompt,
  EditAvailabilityDialog,
  WithdrawAvailabilityDialog,
  type AvailabilityBatchView,
  type AvailabilityOutcome,
  type AvailabilitySubmission,
  type AvailabilityWindowView,
} from "./AvailabilitySurfaces";
import {
  availabilityBatchExpiry,
  availabilityWeekEnd,
  isWithinAvailabilityRenewalHorizon,
  nextAvailabilityWeekStart,
  type AvailabilityMutationEntry,
} from "./availabilityData";

type BaseAvailabilityRead = NonNullable<LivePlanResult["availability"]>[number];

/** The read projection required to render mutation affordances without guessing. */
export type EditableAvailabilityRead = BaseAvailabilityRead;
export type AvailabilityRoutePlan = LivePlanResult;

export function AvailabilityRoute({ result }: { readonly result: AvailabilityRoutePlan }) {
  const settings = useQuery(circleSettingsQuery, { circleId: result.circleId });
  const recordSelf = useMutation(availabilityMutations.recordSelf);
  const recordProxy = useMutation(availabilityMutations.recordProxy);
  const editSelf = useMutation(availabilityMutations.editSelf);
  const editProxy = useMutation(availabilityMutations.editProxy);
  const withdrawSelf = useMutation(availabilityMutations.withdrawSelf);
  const withdrawProxy = useMutation(availabilityMutations.withdrawProxy);
  const { actionsPaused, phase } = useConnectivity();
  const [outcome, setOutcome] = useState<AvailabilityOutcome>({ status: "idle" });
  const [editing, setEditing] = useState<AvailabilityBatchView | null>(null);
  const [withdrawing, setWithdrawing] = useState<AvailabilityBatchView | null>(null);
  const [entryWeekStart, setEntryWeekStart] = useState(() =>
    String(startOfLocalWeek(localDateOf(instant(Date.now()), timeZone(result.timeZone)))),
  );
  const [entryKey, setEntryKey] = useState(0);
  const [projectionNow, setProjectionNow] = useState(() => Date.now());
  const offlineDraft = result.canRecordAvailability ? (
    <AvailabilityDraftPanel
      circleId={result.circleId}
      hideWhenOnlineWithoutDraft
      timeZone={result.timeZone}
      viewerPersonId={result.viewerPersonId}
    />
  ) : null;

  const run = async (
    action: Extract<AvailabilityOutcome, { status: "saved" }>["action"],
    operation: () => Promise<unknown>,
  ): Promise<boolean> => {
    if (actionsPaused) return false;
    setOutcome({ status: "working" });
    try {
      await operation();
      setProjectionNow(Date.now());
      setOutcome({ status: "saved", action });
      return true;
    } catch {
      setOutcome({ status: "refused", message: COPY.errors.serviceUnavailable });
      return false;
    }
  };

  if (settings === undefined) {
    return (
      <>
        <Skeleton label={COPY.loadingLabel} lines={4} />
        {offlineDraft}
      </>
    );
  }
  if (settings === null) {
    return <StatusMessage tone="error">{COPY.errors.serviceUnavailable}</StatusMessage>;
  }
  if (result.availability === null) {
    return <StatusMessage tone="error">{COPY.errors.serviceUnavailable}</StatusMessage>;
  }

  const zone = timeZone(settings.timeZone);
  const now = projectionNow;
  const batches = availabilityBatches(result, result.availability, zone, now);
  const renewal = result.canRecordAvailability
    ? renewalForBatches(
        batches,
        result.viewerPersonId,
        now,
        zone,
        settings.availabilityRenewalHorizonDays,
      )
    : null;
  const people = (result.proxyAvailabilityPeople ?? []).map((person) => ({
    personId: person.personId,
    name: person.displayName,
  }));

  const record = (submission: AvailabilitySubmission) => {
    const entries = mutationEntries(submission.entries);
    void run("record", () =>
      submission.mode === "self"
        ? recordSelf({
            circleId: result.circleId,
            weekStartLocal: submission.weekStartLocal,
            entries,
            idempotencyKey: `availability-record-self-${crypto.randomUUID()}`,
          })
        : recordProxy({
            circleId: result.circleId,
            weekStartLocal: submission.weekStartLocal,
            entries,
            personId: availabilityPersonDocumentId(requiredPersonId(submission)),
            sourceChannel: submission.sourceChannel ?? "message",
            consentConfirmed: true,
            idempotencyKey: `availability-record-proxy-${crypto.randomUUID()}`,
          }),
    );
  };

  return (
    <div className="availability-route">
      {renewal === null ? null : (
        <AvailabilityRenewalPrompt
          actionsDisabled={actionsPaused}
          onRenew={(nextWeekStartLocal) => {
            setEntryWeekStart(nextWeekStartLocal);
            setEntryKey((current) => current + 1);
          }}
          renewal={renewal}
        />
      )}
      <AvailabilityOverview batches={batches} onEdit={setEditing} onWithdraw={setWithdrawing} />
      {phase === "offline" ||
      (!result.canRecordAvailability && !result.canRecordProxyAvailability) ? null : (
        <AvailabilityEntry
          actionsDisabled={actionsPaused}
          blockDefinitions={settings.blockDefinitions}
          canRecordProxy={result.canRecordProxyAvailability}
          canRecordSelf={result.canRecordAvailability}
          initialWeekStart={entryWeekStart}
          key={`${entryWeekStart}:${String(entryKey)}`}
          onSave={record}
          outcome={outcome}
          people={people}
          timeZone={zone}
        />
      )}
      {offlineDraft}
      <EditAvailabilityDialog
        actionsDisabled={actionsPaused || outcome.status === "working"}
        batch={editing}
        blockDefinitions={settings.blockDefinitions}
        onClose={() => {
          setEditing(null);
        }}
        onSave={(entries, sourceChannel) => {
          if (editing?.editMode === undefined || editing.editMode === null) return;
          const operation = () =>
            editing.editMode === "self"
              ? editSelf({
                  circleId: result.circleId,
                  availabilityBatchId: availabilityBatchDocumentId(editing.availabilityBatchId),
                  entries: mutationEntries(entries),
                  idempotencyKey: `availability-edit-self-${crypto.randomUUID()}`,
                })
              : editProxy({
                  circleId: result.circleId,
                  availabilityBatchId: availabilityBatchDocumentId(editing.availabilityBatchId),
                  personId: availabilityPersonDocumentId(editing.personId),
                  sourceChannel: sourceChannel ?? "message",
                  consentConfirmed: true,
                  entries: mutationEntries(entries),
                  idempotencyKey: `availability-edit-proxy-${crypto.randomUUID()}`,
                });
          void run("edit", operation).then((saved) => {
            if (saved) setEditing(null);
          });
        }}
        timeZone={zone}
      />
      <WithdrawAvailabilityDialog
        actionsDisabled={actionsPaused || outcome.status === "working"}
        batch={withdrawing}
        onClose={() => {
          setWithdrawing(null);
        }}
        onConfirm={(sourceChannel) => {
          if (withdrawing?.withdrawMode === undefined || withdrawing.withdrawMode === null) return;
          const operation = () =>
            withdrawing.withdrawMode === "self"
              ? withdrawSelf({
                  circleId: result.circleId,
                  availabilityBatchId: availabilityBatchDocumentId(withdrawing.availabilityBatchId),
                })
              : withdrawProxy({
                  circleId: result.circleId,
                  availabilityBatchId: availabilityBatchDocumentId(withdrawing.availabilityBatchId),
                  personId: availabilityPersonDocumentId(withdrawing.personId),
                  sourceChannel: sourceChannel ?? "message",
                  consentConfirmed: true,
                });
          void run("withdraw", operation).then((saved) => {
            if (saved) setWithdrawing(null);
          });
        }}
      />
    </div>
  );
}

function availabilityBatches(
  result: AvailabilityRoutePlan,
  availability: readonly EditableAvailabilityRead[],
  zone: ReturnType<typeof timeZone>,
  now: number,
): readonly AvailabilityBatchView[] {
  const grouped = new Map<string, EditableAvailabilityRead[]>();
  for (const window of availability) {
    const current = grouped.get(window.availabilityBatchId) ?? [];
    current.push(window);
    grouped.set(window.availabilityBatchId, current);
  }

  return [...grouped.entries()]
    .map(([availabilityBatchId, rows]): AvailabilityBatchView => {
      const first = rows[0];
      if (first === undefined) throw new Error("Availability batch must contain a window.");
      const windows = rows
        .map((row) => windowView(row, zone))
        .sort((left, right) => left.startsAtLocal.localeCompare(right.startsAtLocal));
      const latestEndsAt = Math.max(...rows.map((row) => row.endsAt));
      const expiry = availabilityBatchExpiry(latestEndsAt, now, zone);
      return {
        availabilityBatchId,
        personId: first.personId,
        personName: first.displayName,
        weekStartLocal: first.weekStartLocal,
        weekEndLabel: formatDate(String(availabilityWeekEnd(first.weekStartLocal))),
        expiryDateLocal: expiry.endDateLocal,
        expiryDateLabel: formatDate(String(expiry.endDateLocal)),
        expiresAt: expiry.expiresAt,
        expired: expiry.expired,
        source: first.source,
        recordedByName: first.recordedByName,
        sourceChannel: first.sourceChannel,
        windows,
        editMode: first.editMode,
        withdrawMode: first.withdrawMode,
        commitmentNotice: commitmentNotice(result, first.personId, windows, zone),
      };
    })
    .sort(
      (left, right) =>
        Number(left.expired) - Number(right.expired) ||
        `${right.weekStartLocal}:${right.personName}`.localeCompare(
          `${left.weekStartLocal}:${left.personName}`,
        ),
    );
}

function renewalForBatches(
  batches: readonly AvailabilityBatchView[],
  viewerPersonId: string,
  now: number,
  zone: ReturnType<typeof timeZone>,
  horizonDays: number,
) {
  const latestViewerBatch = batches
    .filter((batch) => batch.personId === viewerPersonId)
    .sort((left, right) => right.expiresAt - left.expiresAt)[0];
  if (
    latestViewerBatch === undefined ||
    !isWithinAvailabilityRenewalHorizon(latestViewerBatch.expiryDateLocal, now, zone, horizonDays)
  ) {
    return null;
  }
  return {
    endLabel: latestViewerBatch.expiryDateLabel,
    expired: latestViewerBatch.expired,
    nextWeekStartLocal: String(nextAvailabilityWeekStart(latestViewerBatch.weekStartLocal)),
  };
}

function windowView(
  row: EditableAvailabilityRead,
  zone: ReturnType<typeof timeZone>,
): AvailabilityWindowView {
  return {
    availabilityWindowId: row.availabilityWindowId,
    startsAtLocal: localInput(row.startsAt, zone),
    endsAtLocal: localInput(row.endsAt, zone),
    dateLabel: formatDate(String(localDateOf(instant(row.startsAt), zone))),
    windowLabel: `${formatTime(row.startsAt, zone)}–${formatTime(row.endsAt, zone)}`,
    helpModeLabels: row.helpModes.map(helpModeLabel),
    helpModes: row.helpModes,
  };
}

function commitmentNotice(
  result: AvailabilityRoutePlan,
  personId: string,
  windows: readonly AvailabilityWindowView[],
  zone: ReturnType<typeof timeZone>,
) {
  const dates = new Set(windows.map((window) => window.startsAtLocal.slice(0, 10)));
  const affected = result.needs.filter(
    (need) =>
      dates.has(String(localDateOf(instant(need.startsAt), zone))) &&
      need.responsible.some((person) => person.personId === personId),
  );
  if (affected.length === 0) return null;
  const affectedDates = new Set(
    affected.map((need) => String(localDateOf(instant(need.startsAt), zone))),
  );
  return {
    count: affected.length,
    dayLabel:
      affectedDates.size === 1 ? formatDate([...affectedDates][0] ?? "") : COPY.severalDates,
  };
}

function mutationEntries(entries: readonly AvailabilityMutationEntry[]) {
  return entries.map((entry) => ({ ...entry, helpModes: [...entry.helpModes] }));
}

function requiredPersonId(submission: AvailabilitySubmission): string {
  if (submission.personId === undefined) throw new Error("Proxy availability needs a person.");
  return submission.personId;
}

function localInput(at: number, zone: ReturnType<typeof timeZone>): string {
  const wall = toWallClock(instant(at), zone);
  return `${String(wall.year).padStart(4, "0")}-${String(wall.month).padStart(2, "0")}-${String(wall.day).padStart(2, "0")}T${String(wall.hour).padStart(2, "0")}:${String(wall.minute).padStart(2, "0")}`;
}

function formatTime(at: number, zone: ReturnType<typeof timeZone>): string {
  const wall = toWallClock(instant(at), zone);
  return `${String(wall.hour).padStart(2, "0")}:${String(wall.minute).padStart(2, "0")}`;
}

function formatDate(dateLocal: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(dateLocal);
  if (match === null) return dateLocal;
  const [, year, month, day] = match;
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))));
}

function helpModeLabel(mode: EditableAvailabilityRead["helpModes"][number]): string {
  if (mode === "in_person") return COPY.helpModes.inPerson;
  if (mode === "driving") return COPY.helpModes.driving;
  return COPY.helpModes.phoneOrOnline;
}
